import { useCampaignProjectLabels } from 'modules/campaigns/lib/hooks/useCampaignProjectLabels';
import { UseQueryGoalsCreatorsAndModifiersState } from 'modules/goals/lib/hooks/useQueryGoalsCreatorsAndModifiers';
import { Goal } from 'modules/goals/types';
import {
  DEFAULT_MISSION_CONTROL_FILTERS_STATE,
  MissionControlFiltersState,
} from 'modules/missionControl/hooks/useMissionControlFilters';
import { UseQueryProjectCampaignAlertsState } from 'modules/missionControl/hooks/useQueryProjectCampaignAlerts';
import { UseQueryCampaignsForMetricsState } from 'modules/missionControl/hooks/useQueryCampaignsForMetrics';
import { UseQueryCampaignsHealthMetricsState } from 'modules/missionControl/hooks/useQueryCampaignsHealthMetrics';
import { UseQueryGoalCreatorsState } from 'modules/missionControl/hooks/useQueryGoalCreators';
import { QueryGoalsReturnValue } from 'modules/missionControl/hooks/useQueryGoals';
import { UseQueryMessageTypesState } from 'modules/missionControl/hooks/useQueryMessageTypes';
import { UseQueryMissionControlUserPreferencesState } from 'modules/missionControl/hooks/useQueryMissionControlUserPreferences';
import { UseQueryProjectGoalsState } from 'modules/missionControl/hooks/useQueryProjectGoals';
import { UseQueryRecentCampaignAlertsState } from 'modules/missionControl/hooks/useQueryRecentCampaignAlerts';
import { TopCampaignsBySystemEventGoalState } from 'modules/missionControl/hooks/useTopCampaignsBySystemEventGoal';
import { TrackFiltersProps } from 'modules/missionControl/hooks/useTrackFilters';
import { createContext, useContext } from 'react';
import noop from 'utils/noop';

type UseCampaignProjectLabelsState = ReturnType<typeof useCampaignProjectLabels>;

export type MissionControlContextState = MissionControlFiltersState & {
  selectedFiltersCount?: number;
  creatorsAndModifiersQuery: UseQueryGoalsCreatorsAndModifiersState;
  projectCampaignAlertsQuery: UseQueryProjectCampaignAlertsState;
  campaignsHealthMetricsQuery: UseQueryCampaignsHealthMetricsState;
  recentCampaignAlertsQuery: UseQueryRecentCampaignAlertsState;
  projectGoalsQuery: UseQueryProjectGoalsState;
  projectLabelsQuery: UseCampaignProjectLabelsState;
  messageTypesQuery: UseQueryMessageTypesState;
  missionControlUserPreferencesQuery: UseQueryMissionControlUserPreferencesState;
  userGoalsQuery: QueryGoalsReturnValue;
  goalCreatorsQuery: UseQueryGoalCreatorsState;
  trackFilters: (filters: TrackFiltersProps) => void;
  campaignsForMetricsQuery: UseQueryCampaignsForMetricsState;
  topCampaignsBySystemEventGoal: TopCampaignsBySystemEventGoalState;
};

export const DEFAULT_MISSION_CONTROL_CONTEXT: MissionControlContextState = {
  ...DEFAULT_MISSION_CONTROL_FILTERS_STATE,
  selectedFiltersCount: 0,
  projectGoalsQuery: {
    errorMessage: null,
    hasError: false,
    isLoading: false,
    projectGoals: [] as Goal[],
    refetch: () => Promise.resolve({ projectGoals: [] } as any),
    total: 0,
  },
  creatorsAndModifiersQuery: {
    errorMessage: undefined,
    goalsCreators: [],
    goalsModifiers: [],
    hasError: false,
    isLoading: false,
    refetch: noop,
  },
  campaignsHealthMetricsQuery: {
    hasError: false,
    isLoading: false,
    metrics: {
      perMediumMetricsAggregations: {},
      totalMetricsAggregations: {},
      prevTotalMetricsAggregations: {},
    },
    refetch: () => Promise.resolve(),
  },
  projectCampaignAlertsQuery: {
    errorMessage: undefined,
    hasError: false,
    isLoading: false,
    projectCampaignAlertsByMedium: {
      Email: [],
      Push: [],
      SMS: [],
      InApp: [],
      WebPush: [],
      Embedded: [],
    },
  },
  recentCampaignAlertsQuery: {
    fetchMore: () => Promise.resolve(),
    hasError: false,
    hasMore: false,
    isFetchingMore: false,
    isLoading: false,
    recentAlerts: [],
    refetch: () => Promise.resolve(),
  },
  projectLabelsQuery: {
    results: [],
    isLoading: false,
    hasError: false,
    errorMessage: undefined,
    refetch: () => Promise.resolve({ campaignLabels: [] } as any),
  },
  messageTypesQuery: {
    messageTypes: [],
    errorMessage: undefined,
    hasError: false,
    isLoading: false,
    refetch: () => Promise.resolve({ messageTypes: [] } as any),
  },
  missionControlUserPreferencesQuery: {} as UseQueryMissionControlUserPreferencesState,
  userGoalsQuery: {
    goals: [],
    hasError: false,
    isLoading: false,
    refetch: () => Promise.resolve([] as any),
  },
  goalCreatorsQuery: {
    errorMessage: undefined,
    goalCreators: [],
    hasError: false,
    isLoading: false,
    refetch: () => Promise.resolve({ goalsCreators: [] } as any),
  },
  trackFilters: noop,
  campaignsForMetricsQuery: {
    campaigns: [],
    errorMessage: undefined,
    hasError: false,
    isLoading: false,
  },
  topCampaignsBySystemEventGoal: {},
};

export const MissionControlContext = createContext<MissionControlContextState>(
  DEFAULT_MISSION_CONTROL_CONTEXT,
);

export const useMissionControlContext = (): MissionControlContextState => {
  const context = useContext(MissionControlContext);

  if (!context) {
    throw new Error('useMissionControlContext must be used within a MissionControlProvider');
  }
  return context;
};
